/*
Description
    Thin API wrapper for the Grower Intake Estimates tab (Almondco - 2116 - Grower
    Intake Estimates Master).

History
    02Sep2026 (SS)
        - #76611: UI - Grower Intake Estimates List (grower-intake-estimate.vue)
    02Sep2026 (SS)
        - #76646: UI - Grower Intake Estimate detail panel (Review/Edit,
          Huller/Cracker field, Variety/Estimate grid) - added getEstimateDetail,
          getVarietyOptions, saveEstimate.
    02Sep2026 (SS)
        - #76651: UI - Add New Season picker (unused Seasons) - added
          getSeasonOptions, getUsedSeasons.

(c) Copyright Graycorp Pty Ltd (ABN 12 082 807 761). All Rights Reserved.
*/
import ApiService from "../api.service";
import appConfig from "../../../app-config";

ApiService.init(appConfig.VUE_APP_ROOT_API);

const AlmondcoGrowerIntakeEstimateService = {
  // §5/§9 (Story 1): AlmondcoGrowerIntakeEstimateSummary, filtered to the
  // currently-loaded Supplier's own Code (the Grower). One row per Season this
  // Grower already has a saved estimate for - a Season with no estimate yet
  // never appears here.
  dataSourceConfig(url, params) {
    params.key = ["SeasonYear"];
    params.keyType = {
      SeasonYear: "Int32",
    };

    return ApiService.createDataSource(url, params);
  },

  // §5: reused, unmodified api/Supplier endpoint - active H1/H2 rows only
  // (BR-04). Loaded once, not per-row: the List's own Huller/Cracker column is
  // a read-only display lookup, not an editable field (editing is Story 3's
  // scope).
  async getHullerOptions() {
    const requestScreenData = {
      url: "api/Supplier",
      select: ["Code,Name"],
      filter:
        "(SupplierTypeCode eq 'H1' or SupplierTypeCode eq 'H2') and Inactive eq 'N'",
      orderby: "Code asc",
    };

    return await ApiService.oDataGet(requestScreenData);
  },

  // §5: reused, unmodified api/ProductCategory1 endpoint - active rows only
  // (BR-06(a)). Loaded once, not per-row - the Variety/Estimate grid's own
  // lookup option list is Grower+Season-independent.
  //
  // #E2E fix (02Sep2026): ProductCategory1's real display column is
  // Description, not Name - confirmed against GcorpAPI.Models.ProductCategory1
  // ($select=Code,Name 400'd with "Could not find a property named 'Name'"),
  // caught running a live Playwright pass over this story.
  async getVarietyOptions() {
    const requestScreenData = {
      url: "api/ProductCategory1",
      select: ["Code,Description"],
      filter: "Inactive eq 'N'",
      orderby: "Code asc",
    };

    return await ApiService.oDataGet(requestScreenData);
  },

  // §5: the detail panel's own read - api.AlmondcoGrowerIntakeEstimate shim,
  // filtered to the selected Grower+Season (dataSourceBuilder(selectedRecord)
  // equivalent - fetched directly here since the grid manages its rows as a
  // local, mutable array for Add/Remove via DxEditing's native batch editing,
  // matching the sibling AlmondcoGrowerPoolSplitService.getGrowerSplit).
  async getEstimateDetail(growerCode, seasonYear) {
    var url = `api/AlmondcoGrowerIntakeEstimate?$filter=GrowerCode eq '${growerCode}' and SeasonYear eq ${seasonYear}`;
    return await ApiService.get(url);
  },

  // §5: POST api/Save_AlmondcoGrowerIntakeEstimate - full delete/update/insert
  // reconciliation (BR-05). Returns the Grower+Season's fresh saved row set on
  // success (each row's audit columns included), consumed directly instead of
  // a follow-up GET.
  async saveEstimate(payload) {
    return await ApiService.post("api/Save_AlmondcoGrowerIntakeEstimate", payload);
  },

  // §5/§9 (Story 2): reused, unmodified api/AlmondcoIntakeSeason endpoint -
  // FLAG-IE-01 resolved: exposes all available Season values, no current/
  // next-year restriction.
  async getSeasonOptions() {
    const requestScreenData = {
      url: "api/AlmondcoIntakeSeason",
      select: ["Season"],
      orderby: "Season desc",
    };

    return await ApiService.oDataGet(requestScreenData);
  },

  // §6/§9 (Story 2): Seasons this Grower already has a saved estimate for -
  // the + ADD NEW picker's candidate set is all Season values minus this list
  // (client-side set subtraction; no server-side "unused Seasons" endpoint
  // exists or is proposed).
  async getUsedSeasons(growerCode) {
    const requestScreenData = {
      url: "api/AlmondcoGrowerIntakeEstimateSummary",
      select: ["SeasonYear"],
      filter: `GrowerCode eq '${growerCode}'`,
    };

    return await ApiService.oDataGet(requestScreenData);
  },
};

export { AlmondcoGrowerIntakeEstimateService };
